import { plugin, debug } from 'light-component';
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Block, t } from '@common';
import { Require, Select } from '../component';
import { Base, useBreakpoint } from '../../..';
import styles from './style.module.less';
import type { FormVersionProps, VersionOptions } from './types';

export function WidgetFormVersion(props: FormVersionProps): ReactElement {
  debug.widget('dashboard formVersion', props);
  const { md } = useBreakpoint();

  const { isSingle = false } = props;
  const [formValue, setFormValue] = useState<string[]>([]);
  const [singleFormValue, setSingleFormValue] = useState<string | null>(null);
  const [versionValue, setVersionValue] = useState<string[]>([]);
  const [curVersionOptions, setCurVersionOptions] = useState<VersionOptions[]>([]);
  const selectChange = useRef(false);

  const formOptions = useMemo(() => {
    if (props.formOptions?.length > 0) {
      let options = props.formOptions?.map(item => ({
        value: item.value,
        label: item.title
      }));
      const uniqueValue = new Set(options?.map(item => item.value));
      options = options?.filter(item => uniqueValue.delete(item.value));
      options.sort((itemA, itemB) => itemA.label.localeCompare(itemB.label));
      return options;
    }
    return [];
  }, [props.formOptions]);

  const versionOptions = useMemo(() => {
    if (props.versionOptions?.length > 0) {
      let options = props.versionOptions?.map(item => ({
        value: item.value,
        formId: item.formId,
        label: t(item.label)
      }));
      const uniqueValue = new Set(options?.map(item => item.value));
      options = options?.filter(item => uniqueValue.delete(item.value));
      return options;
    }
    return [];
  }, [props.versionOptions]);

  useEffect(() => {
    setCurVersionOptions(versionOptions.filter(item => {
      if (!isSingle) {
        return formValue?.includes(item.formId);
      }
      return singleFormValue === item.formId;
    }));
  }, [versionOptions, formValue, singleFormValue]);

  useEffect(() => {
    if (selectChange.current) {
      selectChange.current = false;
      return;
    }
    if (props.value != null) {
      if (!isSingle) {
        const formValue = new Set<string>([]);
        versionOptions.forEach(item => {
          if (props.value.includes(item.value)) {
            formValue.add(item.formId);
          }
        });
        setFormValue([...formValue]);
      } else {
        let formValue = null;
        versionOptions.forEach(item => {
          if (props.value.includes(item.value)) {
            formValue = item.formId;
          }
        });
        setSingleFormValue(formValue);
      }
      setVersionValue([...props.value]);
    } else {
      setFormValue([]);
      setSingleFormValue(null);
      setVersionValue([]);
    }
  }, [props.value]);

  const handleFormChange = (newValue: string[]): void => {
    selectChange.current = true;
    const value = versionOptions.filter(item => newValue.includes(item.formId)).map(item => item.value);
    if (newValue?.length === 1) {
      setVersionValue([...value]);
    } else {
      setVersionValue([]);
    }
    setFormValue(newValue);
    props.onChange?.(value);
  };

  const handleSingleFormChange = (newValue: string): void => {
    selectChange.current = true;
    const value = versionOptions.filter(item => newValue === item.formId).map(item => item.value);
    setVersionValue([...value]);
    setSingleFormValue(newValue);
    props.onChange?.(value);
  };

  const handleVersionChange = (newValue: string[]): void => {
    selectChange.current = true;
    if (newValue.length === 0) {
      const value = versionOptions.filter(item => {
        if (isSingle) {
          return singleFormValue === item.formId;
        }
        return formValue.includes(item.formId);
      }).map(item => item.value);

      props.onChange?.(value);
    } else {
      props.onChange?.(newValue);
    }
    setVersionValue(newValue);
  };

  return (
    <div className={props.className ?? styles.propertyItem}>
      <div className={styles.input}>
        <Require require={props.require} />
        <span className={styles.title}>{t(props.title)}</span>
      </div>
      <Base.Space>
        <Block if={!isSingle}>
          <Select
            className={styles.formSelectBox}
            mode='multiple'
            value={formValue}
            placeholder={t(props.placeholder ?? '')}
            options={formOptions}
            selectAll={formValue?.length === formOptions.length}
            allowClear
            onChange={handleFormChange}
          />
        </Block>
        <Block if={isSingle}>
          <Base.Select
            className={styles.formSelectBox}
            value={singleFormValue}
            placeholder={t(props.placeholder ?? '')}
            options={formOptions}
            allowClear
            showSearch
            optionFilterProp='label'
            onChange={handleSingleFormChange}
            popupMatchSelectWidth={false}
            dropdownStyle={md === false ? { maxWidth: 300 } : { maxWidth: 600 }}
          />
        </Block>
        <Block if={formValue?.length === 1 || singleFormValue !== null}>
          <Select
            className={styles.formSelectBox}
            mode='multiple'
            value={versionValue}
            placeholder={t('选择版本')}
            options={curVersionOptions}
            selectAll={versionValue?.length === curVersionOptions.length}
            allowClear
            onChange={handleVersionChange}
          />
        </Block>
      </Base.Space>
    </div>
  );
}

plugin.register({ kind: 'widget', type: 'formVersion', component: WidgetFormVersion });
